import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Container, Divider, Button, Typography, InputBase, Accordion, AccordionSummary, AccordionDetails, IconButton, Tooltip, Badge } from '@mui/material';
import { makeStyles, useTheme } from '@mui/styles';
import { Add, ExpandMore, FilterAlt, Input, Search, Sort, Visibility, VisibilityOff } from '@mui/icons-material';
import { useSocket } from '../../context/useSocket';
import { selectUser } from '../../reducers/userSlice';
import { isRTFOAdminUser } from './rtfoConfig';

const useStyles = makeStyles((theme) => ({
  root: {
    // backgroundColor: 'red',
    // backgroundColor: theme.palette.backgroundOne,
    // backgroundColor: theme.palette.grey[100],
    flexBasis: 320,
    flexShrink: 0,
    flexGrow: 0,
    height: '100%',
    marginTop: 0,
    padding: 10,
    overflowY: 'auto',
    overflowX: 'hidden',
    position: 'relative'
    // borderRight: '1px solid lightgrey'
  },
  listsButtons: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: 10,
    marginBottom: 10
  },
  button: {
    borderRadius: 20
  },
  search: {
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    marginBottom: 5,
    paddingLeft: 5,
    // border: `1px solid ${theme.palette.primary.main}`,
    height: 36,
  },
  searchIcon: {
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    // color: theme.palette.primary.main
  },
  inputRoot: {
    color: 'inherit',
  },
  inputInput: {
    // vertical padding + font size from searchIcon
    paddingLeft: 24,
  },
  emptyForecast: {
    width: '100%',
    height: '80%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center'
  }
}));

export const RTFOPadsListContainer = ({ padActivity, formations, setCreateNewPadOpen, handleSelectPad, currentPadName, handleTogglePadVisibility, visiblePadNames, filters, handlePadsListFilterVisible }) => {
  const classes = useStyles();
  const theme = useTheme();

  const { socket } = useSocket();

  const user = useSelector(selectUser);
  const darkMode = user?.value?.darkMode;

  const [searchPad, setSearchPad] = useState("");
  const [sortDesc, setSortDesc] = useState(true);
  const [expandAll, setExpandAll] = useState(true);
  const [expandedFormations, setExpandedFormations] = useState(formations);

  function handleSort() {
    setSortDesc(prev => !prev);
  };
  function handleExpandCollapseAll() {
    if (expandAll) {
      setExpandAll(false);
      setExpandedFormations([]);
    } else {
      setExpandAll(true);
      setExpandedFormations(formations);
    };
  };
  function handleExpandCollapseFormation(formation) {
    if (expandedFormations.includes(formation)) {
      setExpandedFormations(prev => prev.filter(f => f !== formation));
    } else {
      setExpandedFormations(prev => [...prev, formation]);
    };
  };

  let sortedPads = [...padActivity];
  if (sortDesc) {
    sortedPads = sortedPads?.sort((a,b) => new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime());
  } else {
    sortedPads = sortedPads?.sort((a,b) => new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime());
  }
  if (searchPad?.length > 0) {
    sortedPads = sortedPads.filter(pad => pad.name?.toLowerCase()?.replace('-', '').includes(searchPad.toLowerCase().replace('-', '')))
  };
  
  let sortedFormations = [...formations];
  sortedFormations = sortedFormations?.sort((a, b) => a.localeCompare(b));

  try {
    return (
      <Container className={classes.root} style={{
        backgroundColor: theme?.palette?.elevationOne?.[darkMode ? 'dark' : 'light'],
        borderRight: `1px solid ${darkMode ? 'dimgrey' : 'lightgrey'}`
      }}>
        <div id="forecast-header" style={{display: 'flex', flexDirection: 'column'}}>
          <Typography variant="h6" color="textPrimary">
            RTFO PADS
          </Typography>
        </div>
  
        <div className={classes.listsButtons}>
          <Button startIcon={<Add />} variant="outlined" size="small" style={{height: 28}} onClick={() => setCreateNewPadOpen(true)} disabled={!isRTFOAdminUser(user?.value?.email)}>
            New Pad
          </Button>
          <div>
            <Button startIcon={<Sort /> } onClick={handleSort} color={darkMode ? "inherit" : "primary"} size="small">
              Sort
            </Button>
            <Button startIcon={<Badge badgeContent={filters ? Object.keys(filters).length : 0} color="primary"><FilterAlt /></Badge> } onClick={handlePadsListFilterVisible} color={darkMode ? "inherit" : "primary"} size="small">
              Filter
            </Button>
          </div>
        </div>
  
        <div className={classes.search} style={{border: `1px solid ${darkMode ? "lightgrey" : theme.palette.primary.main}`}}>
          <div className={classes.searchIcon}>
            <Search color={darkMode ? "inherit" : "primary"} />
          </div>
          <InputBase
            placeholder="Search pads by name"
            classes={{
              root: classes.inputRoot,
              input: classes.inputInput,
            }}
            value={searchPad}
            onChange={handlePadSearch}
          />
        </div>

        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <Typography fontSize={'10px'} style={{marginBottom: 5}}>{`Pads: ${sortedPads?.length || 0} | Wells: ${sortedPads?.map(pad => Object.keys(pad?.wellMapping || {}))?.flat(Infinity)?.length} | Sorted by: ${sortDesc ? 'newest' : 'oldest'}`}</Typography>
          <Button color={darkMode ? "inherit" : "primary"} size="small" onClick={handleExpandCollapseAll}><Typography fontSize={10}>{expandAll ? 'Collapse' : 'Expand'} All</Typography></Button>
        </div>
  
        <Divider />
  
        {sortedFormations?.length > 0 ? <div className={classes.lists}>  
          {sortedFormations.map(formation => {
            return (
              <RTFOPadsListItem 
                padActivity={sortedPads} 
                formation={formation} 
                darkMode={darkMode} 
                theme={theme} 
                socket={socket}
                userEmail={user?.value?.email}
                handleSelectPad={handleSelectPad}
                expanded={expandedFormations.includes(formation)}
                handleExpandCollapseFormation={handleExpandCollapseFormation}
                currentPadName={currentPadName}
                handleTogglePadVisibility={handleTogglePadVisibility}
                visiblePadNames={visiblePadNames}
                key={formation}
              />
            )
          })}
        </div>
        :
        <div className={classes.emptyForecast}>
          <Typography variant="subtitle" color="textSecondary" textAlign="center">Create new pad to get started</Typography>
        </div>
        }

      </Container>
    );  
  } catch (error) {
    console.log(error);
    return <></>;
  };

  // FUNCTIONS
  function handlePadSearch(e) {
    setSearchPad(e.target.value);
  };
  
}

const RTFOPadsListItem = ({ padActivity, formation, darkMode, theme, expanded, socket, userEmail, handleSelectPad, handleExpandCollapseFormation, currentPadName, handleTogglePadVisibility, visiblePadNames })  => {
  
  const form = formation.split('-')?.[0]?.trim();
  const area = formation.split('-')?.[1]?.trim();
  const formationPads = padActivity?.filter(pad => form === pad.formation && area === pad.area);

  try {
    return (
      formationPads.length > 0 ?
      <Accordion 
        // defaultExpanded
        expanded={expanded}
        style={{
          marginTop: 10
        }}
        sx={{
          boxShadow: 1,
          backgroundColor: theme?.palette?.elevationOne?.[darkMode ? 'dark' : 'light']
        }}
        onClick={(e) => {
          
        }}
        elevation={0}
        disableGutters
      >
        <AccordionSummary expandIcon={<ExpandMore />} onClick={() => handleExpandCollapseFormation(formation)}>
          <div style={{width: 240, textOverflow: 'ellipsis'}}><Typography noWrap>{formation}</Typography></div>
        </AccordionSummary>
        <AccordionDetails style={{margin: 0, padding: 0}}>
          {formationPads?.map(pad => {
            return (
              <div key={pad.name} >
                <RTFOPadsListPad 
                  pad={pad} 
                  socket={socket} 
                  userEmail={userEmail} 
                  handleSelectPad={handleSelectPad} 
                  currentPadName={currentPadName} 
                  handleTogglePadVisibility={handleTogglePadVisibility} 
                  visiblePadNames={visiblePadNames}
                />
                <Divider />
              </div>
              
            )
          })}
        </AccordionDetails>
  
      </Accordion> :
      <div></div>
    )
  } catch (error) {
    return <></>
  }
}

const RTFOPadsListPad = ({ pad, socket, userEmail, handleSelectPad, currentPadName, handleTogglePadVisibility, visiblePadNames }) => {
  const isPadSelected = currentPadName === pad?.name;
  const isPadVisible = visiblePadNames?.includes(pad?.name);

  return (
    <div style={{
        height: 40, 
        display: 'flex', 
        flexDirection: 'row', 
        width: '100%', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        cursor: 'pointer', 
        backgroundColor: isPadSelected ? 'lightgrey' : '#fff'
      }} onClick={() => handleSelectPad(pad?.name)}>
      <div style={{height: '100%', display: 'flex', flexDirection: 'row', opacity: !isPadVisible && 0.5}}>
        <div style={{height: '100%', width: 4, backgroundColor: pad?.isActive ? 'green' : 'lightgrey'}}>
        </div>
        <div style={{marginLeft: 10, display: 'flex', flexDirection: 'column'}}>
          <Typography style={{fontSize: 16}}>{pad?.name}</Typography>
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            <Typography style={{fontSize: 10}}>Status: {pad?.isActive ? 'Active' : 'Completed'}</Typography>
            <Typography style={{fontSize: 10, marginLeft: 5}}>|</Typography>
            <Typography style={{fontSize: 10, marginLeft: 5}}>Wells: {pad?.wellMapping ? Object.keys(pad?.wellMapping)?.length : 0}</Typography>
            <Typography style={{fontSize: 10, marginLeft: 5}}>|</Typography>
            <Typography style={{fontSize: 10, marginLeft: 5}}>Stages: {(pad?.stages)?.length || 0}</Typography>
          </div>
        </div>
      </div>

      <div style={{display: 'flex', flexDirection: 'row'}}>
        <Tooltip title="Toggle Pad Visibility">
          <IconButton 
            size="small"
            onClick={(e) => handleTogglePadVisibility(e, pad?.name)}
            style={{width: 32, height: 32, margin: 0}}
            color="primary"
          >
            {isPadVisible ? <Visibility fontSize='16px'/> : <VisibilityOff fontSize='16px'/>}
          </IconButton>
        </Tooltip>

        <Tooltip title="Load Pad Data">
          <IconButton 
            size="small"
            onClick={(e) => handleLoadPadDataClick(e, pad?.name, socket, userEmail)}
            style={{width: 32, height: 32, margin: 0}}
            color="primary"
          >
            <Input fontSize='16px'/>
          </IconButton>
        </Tooltip>

      </div>

    </div>

  )
}

function handleLoadPadDataClick(e, padName, socket, userEmail) {
  e.stopPropagation();

  if (!padName || !socket || !socket.readyState === 1) return;

  socket?.send(JSON.stringify({
    messageType: 'getPadData',
    padName,
    initialLoad: true,
    getFullData: userEmail === 'bhepburn@wcap.ca' ? true : false,
    userEmail
  }));
};